import {
  Component,
  computed,
  DestroyRef,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize, Subject, takeUntil } from "rxjs";
import { CdkDragDrop } from "@angular/cdk/drag-drop";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SharedModule } from "../../../shared/shared.module";
import {
  Contact,
  ContactGroup,
  ContactReference,
  ContactSource,
  ExtensionContact,
  ManualContact,
} from "../../interfaces/contact.interface";
import {
  IAddressBookDialogData,
  IAddressBookDialogResult,
  IContactGroupCreateData,
  IContactWidget,
} from "../../interfaces/contact-widget.interface";
import { ContactUtils } from "../../utils/contact-utils";
import { ContactWidgetService } from "../../services/contact-widget.service";
import { AddressBookDialogComponent } from "../contact-group-edit/address-book/address-book.component";
import { TranslateService } from "@ngx-translate/core";
import { NgModel } from "@angular/forms";

@Component({
  selector: "lib-contact-group-create",
  imports: [SharedModule],
  templateUrl: "./contact-group-create.component.html",
  styleUrl: "./contact-group-create.component.scss",
})
export class ContactGroupCreateComponent implements OnInit, OnDestroy {
  // Helper ContactUtils
  protected readonly ContactUtils: typeof ContactUtils = ContactUtils;

  // Expose ContactSource for template
  protected readonly ContactSource = ContactSource;

  // Services
  private readonly contactWidgetService: ContactWidgetService =
    inject(ContactWidgetService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly translate: TranslateService = inject(TranslateService);

  @ViewChild("groupNameInput") groupNameInput!: NgModel;

  // Form data
  groupName: string = "";
  groupNameError: string = "";
  isInvalid: boolean = false;
  selectedContactIds: ContactReference[] = [];
  contactWidget: IContactWidget | undefined;

  // Contact data - reactive signals
  allContacts: WritableSignal<Contact[]> = signal([]);
  isLoadingExtensionContacts: WritableSignal<boolean> = signal(false);

  // Sorting and filtering
  sortBy: WritableSignal<"selection" | "alphabetical"> = signal("selection");
  searchTerm: WritableSignal<string> = signal("");

  // Track changes to manual contacts
  private manualContactsChanged: boolean = false;
  private readonly createdManualContacts: ManualContact[] = [];
  private updatedManualContacts: ManualContact[] = [];
  private readonly deletedManualContactIds: string[] = [];

  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();

  // Computed getter for filtered and sorted contacts (kept for compatibility but not used in new design)
  protected displayedContacts: Signal<Contact[]> = computed(() => {
    let contacts = this.allContacts();

    // Apply search filter
    const searchTerm = this.searchTerm().toLowerCase();
    if (searchTerm) {
      contacts = contacts.filter(
        (contact) =>
          contact.name?.toLowerCase().includes(searchTerm) ||
          ContactUtils.getDisplayPhoneNumber(contact)
            .toLowerCase()
            .includes(searchTerm),
      );
    }

    // Apply sorting
    const sortBy = this.sortBy();
    if (sortBy === "selection") {
      // Selected contacts first, then alphabetical within each group
      const selected = contacts
        .filter((contact: Contact) => this.isContactSelected(contact))
        .sort((a: Contact, b: Contact) => a.name.localeCompare(b.name));
      const unselected = contacts
        .filter((contact: Contact) => !this.isContactSelected(contact))
        .sort((a: Contact, b: Contact) => a.name.localeCompare(b.name));
      return [...selected, ...unselected];
    } else {
      // Pure alphabetical sorting
      return contacts.sort((a: Contact, b: Contact) =>
        a.name.localeCompare(b.name),
      );
    }
  });

  constructor(
    public dialogRef: MatDialogRef<ContactGroupCreateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IContactGroupCreateData,
  ) {
    this.contactWidget = data.widget;
  }

  ngOnInit(): void {
    // Initialize with contacts passed from parent (existing manual contacts)
    this.allContacts.set([...this.data.contacts]);

    // Setup socket connection monitoring
    this.setupSocketConnectionMonitoring();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private setupSocketConnectionMonitoring(): void {
    if (!this.contactWidget?._id) {
      console.error("[ContactGroupCreate] Widget ID is required");
      return;
    }

    this.contactWidgetService
      .getConnectionState()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((state: "disconnected" | "connecting" | "connected") => {
        if (state === "connected") {
          this.loadAllExtensionContacts();
        }
      });
  }

  private loadAllExtensionContacts(): void {
    if (!this.contactWidget?._id) {
      return;
    }

    this.isLoadingExtensionContacts.set(true);

    this.contactWidgetService
      .requestAllExtensionContacts(this.contactWidget._id)
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        finalize(() => this.isLoadingExtensionContacts.set(false)),
      )
      .subscribe({
        next: (extensionContacts: ExtensionContact[]) => {
          console.log(
            `[ContactGroupCreate] Received ${extensionContacts.length} extension contacts`,
          );

          // Merge with existing contacts (avoid duplicates)
          const currentContacts = this.allContacts();
          const extensionContactsToAdd = extensionContacts.filter(
            (extContact: ExtensionContact) =>
              !currentContacts.some(
                (existing: Contact) =>
                  existing.contactId === extContact.contactId &&
                  existing.source === extContact.source,
              ),
          );

          this.allContacts.set([...currentContacts, ...extensionContactsToAdd]);
        },
        error: (error) => {
          console.error(
            "[ContactGroupCreate] Error loading extension contacts:",
            error,
          );
          this.snackBar.open(
            this.translate.instant(
              "ASTIMAX.CONTACT.WIDGET.FAILED_TO_LOAD_EXTENSION_CONTACTS",
            ),
            "Close",
            { duration: 3000 },
          );
        },
      });
  }

  // Sorting methods
  setSortBy(sortType: "selection" | "alphabetical"): void {
    const previousSort = this.sortBy();
    this.sortBy.set(sortType);

    // Provide user feedback about the sorting change
    if (previousSort !== sortType && this.selectedContactIds.length > 0) {
      const message =
        sortType === "alphabetical"
          ? this.translate.instant(
              "ASTIMAX.CONTACT.WIDGET.CONTACTS_SORTED_ALPHABETICALLY",
            )
          : this.translate.instant(
              "ASTIMAX.CONTACT.WIDGET.CONTACTS_SORTED_BY_SELECTION_ORDER",
            );

      this.snackBar.open(message, "", { duration: 1500 });
    }
  }

  // Get the display position for a contact based on current sort mode
  getContactDisplayPosition(
    contactRef: ContactReference,
    index: number,
  ): number {
    if (this.sortBy() === "alphabetical") {
      // In alphabetical mode, show alphabetical position but keep original selection order intact
      return index + 1;
    } else {
      // In selection mode, show the actual selection order
      return (contactRef.order ?? index) + 1;
    }
  }

  // Contact selection methods
  isContactSelected(contact: Contact): boolean {
    return this.selectedContactIds.some(
      (ref: ContactReference) =>
        ref.contactId === contact.contactId && ref.source === contact.source,
    );
  }

  // Address Book Methods (similar to ContactGroupEdit)
  openAddressBook(source: ContactSource): void {
    const data: IAddressBookDialogData = {
      source: source,
      widget: this.contactWidget!,
      alreadySelectedContactIds: this.selectedContactIds,
      existingContacts: this.allContacts(),
    };

    const dialogRef = this.dialog.open(AddressBookDialogComponent, {
      panelClass: ["astimax-address-book-dialog", "adx-dialog-large"],
      disableClose: true,
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IAddressBookDialogResult | undefined) => {
        if (
          result &&
          typeof result === "object" &&
          result.selectedContacts.length > 0
        ) {
          this.addContactsFromAddressBook(result.selectedContacts);
        }

        // Handle manual contact changes from address book
        if (result?.manualContactChanges) {
          this.handleManualContactChanges(result.manualContactChanges);
        }
      });
  }

  private addContactsFromAddressBook(contacts: Contact[]): void {
    // Add contacts to local contact list if they don't exist
    const currentContacts = this.allContacts();
    const contactsToAdd = contacts.filter(
      (newContact: Contact) =>
        !currentContacts.some(
          (existing: Contact) =>
            existing.contactId === newContact.contactId &&
            existing.source === newContact.source,
        ),
    );

    if (contactsToAdd.length > 0) {
      this.allContacts.set([...currentContacts, ...contactsToAdd]);
    }

    // Add contact references to selection
    const newReferences: ContactReference[] = contacts.map(
      (contact: Contact) => ({
        contactId: contact.contactId,
        source: contact.source!,
        order: this.selectedContactIds.length, // Add at the end
      }),
    );

    // Filter out already selected contacts
    const referencesToAdd = newReferences.filter(
      (newRef: ContactReference) =>
        !this.selectedContactIds.some(
          (existing: ContactReference) =>
            existing.contactId === newRef.contactId &&
            existing.source === newRef.source,
        ),
    );

    // Add new references with proper order
    let currentOrder = this.selectedContactIds.length;
    const referencesWithOrder = referencesToAdd.map((ref) => ({
      ...ref,
      order: currentOrder++,
    }));

    this.selectedContactIds = [
      ...this.selectedContactIds,
      ...referencesWithOrder,
    ];

    this.snackBar.open(
      `${this.translate.instant("ASTIMAX.CONTACT.WIDGET.ADDED")} ${referencesToAdd.length} ${referencesToAdd.length === 1 ? this.translate.instant("ASTIMAX.CONTACT.WIDGET.CONTACT") : this.translate.instant("ASTIMAX.CONTACT.WIDGET.CONTACTS")} ${this.translate.instant("ASTIMAX.CONTACT.WIDGET.TO_GROUP")}`,
      "Close",
      { duration: 3000 },
    );
  }

  private handleManualContactChanges(changes: any): void {
    // Track all manual contact changes
    if (changes.created.length > 0) {
      this.createdManualContacts.push(...changes.created);
      this.manualContactsChanged = true;

      // Add newly created contacts to allContacts signal
      const currentContacts = this.allContacts();
      const newContacts = changes.created.filter(
        (newContact: ManualContact) =>
          !currentContacts.some(
            (existing: Contact) =>
              existing.contactId === newContact.contactId &&
              existing.source === newContact.source,
          ),
      );

      if (newContacts.length > 0) {
        this.allContacts.set([...currentContacts, ...newContacts]);
      }
    }

    if (changes.updated.length > 0) {
      changes.updated.forEach((updatedContact: ManualContact) => {
        const existingUpdateIndex = this.updatedManualContacts.findIndex(
          (c: ManualContact) => c.contactId === updatedContact.contactId,
        );
        if (existingUpdateIndex > -1) {
          this.updatedManualContacts[existingUpdateIndex] = updatedContact;
        } else {
          this.updatedManualContacts.push(updatedContact);
        }
      });
      this.manualContactsChanged = true;

      // Update contacts in allContacts signal
      const currentContacts = this.allContacts();
      const updatedContacts = currentContacts.map((contact: Contact) => {
        const updatedContact = changes.updated.find(
          (updated: ManualContact) =>
            updated.contactId === contact.contactId &&
            contact.source === ContactSource.MANUAL,
        );
        return updatedContact ?? contact;
      });

      this.allContacts.set(updatedContacts);
    }

    if (changes.deleted.length > 0) {
      this.deletedManualContactIds.push(...changes.deleted);
      this.manualContactsChanged = true;

      // Remove deleted contacts from selection if they were selected
      // This ensures that deleted manual contacts are properly removed from the group
      const originalSelectionLength = this.selectedContactIds.length;
      this.selectedContactIds = this.selectedContactIds.filter(
        (ref: ContactReference) =>
          !(
            ref.source === ContactSource.MANUAL &&
            changes.deleted.includes(ref.contactId)
          ),
      );

      // Log if contacts were removed from selection
      const removedFromSelection =
        originalSelectionLength - this.selectedContactIds.length;
      if (removedFromSelection > 0) {
        console.log(
          `[ContactGroupCreate] Removed ${removedFromSelection} deleted manual contact(s) from group selection`,
        );
      }

      // Normalize order numbers after removal
      this.selectedContactIds = ContactUtils.normalizeOrderNumbers(
        this.selectedContactIds,
      );

      // Remove deleted contacts from allContacts signal
      const currentContacts = this.allContacts();
      const filteredContacts = currentContacts.filter(
        (contact: Contact) =>
          !(
            contact.source === ContactSource.MANUAL &&
            changes.deleted.includes(contact.contactId)
          ),
      );

      this.allContacts.set(filteredContacts);
    }

    // Update widget settings if manual contacts were changed
    if (this.manualContactsChanged && this.contactWidget) {
      // Update widget manual contacts with all changes
      const currentManualContacts = this.allContacts().filter(
        (c: Contact): c is ManualContact => ContactUtils.isManualContact(c),
      );

      if (this.contactWidget.settings) {
        this.contactWidgetService.updateManualContacts(
          this.contactWidget._id,
          currentManualContacts,
          true,
        );
        this.contactWidgetService
          .saveContactWidgetSettingsToDatabase(
            this.contactWidget._id,
            this.contactWidget,
          )
          .then();
      }
    }
  }

  // Contact Reordering
  onContactReorder(event: CdkDragDrop<ContactReference[]>): void {
    // When dragging, we need to update the actual selectedContactIds array
    // Get the current sorted list
    const sortedContacts = this.getSortedSelectedContacts();

    // Use utility to move contact and update all order numbers
    // Update the main selectedContactIds array
    this.selectedContactIds = ContactUtils.moveContactToPosition(
      sortedContacts,
      sortedContacts[event.previousIndex],
      event.currentIndex,
    );

    // If we were in alphabetical mode, switch back to selection order since user manually reordered
    if (this.sortBy() === "alphabetical") {
      this.sortBy.set("selection");
    }
  }

  // Get sorted selected contacts based on current sort mode
  getSortedSelectedContacts(): ContactReference[] {
    const contacts = [...this.selectedContactIds];

    if (this.sortBy() === "alphabetical") {
      // Sort alphabetically by contact name
      return contacts.sort((a, b) => {
        const contactA = this.getContactFromReference(a);
        const contactB = this.getContactFromReference(b);

        if (!contactA || !contactB) return 0;

        return contactA.name.localeCompare(contactB.name);
      });
    } else {
      // Sort by selection order (order property)
      return ContactUtils.sortContactReferences(contacts);
    }
  }

  // Helper methods for safe contact property access
  getContactFromReference(contactRef: ContactReference): Contact | undefined {
    return this.allContacts().find(
      (contact: Contact) =>
        contact.contactId === contactRef.contactId &&
        contact.source === contactRef.source,
    );
  }

  getDisplayPhoneNumberSafe(contact: Contact | undefined): string {
    if (!contact) return "";

    // Handle extension contacts with number property
    if (
      contact.source === ContactSource.EXTENSION &&
      "number" in contact &&
      contact.number
    ) {
      return contact.number;
    }

    // Fallback to phoneNumber for all contact types
    return contact.phoneNumber || "";
  }

  getSelectedContactAriaLabel(
    contactRef: ContactReference,
    index: number,
  ): string {
    const contact = this.getContactFromReference(contactRef);
    if (!contact) return "Unknown contact";

    const position = this.getContactDisplayPosition(contactRef, index);
    const sortMode =
      this.sortBy() === "alphabetical" ? "alphabetical" : "selection";

    return `${contact.name}, position ${position} of ${this.selectedContactIds.length} (${sortMode} order), ${this.getDisplayPhoneNumberSafe(contact)}, ${ContactUtils.getContactSourceLabel(contactRef.source, this.translate)}`;
  }

  removeSelectedContact(contactRef: ContactReference): void {
    this.selectedContactIds = ContactUtils.removeContact(
      this.selectedContactIds,
      contactRef,
    );
  }

  // Statistics Methods for Address Book Cards
  getManualContactsCount(): number {
    return this.allContacts().filter((c: Contact) =>
      ContactUtils.isManualContact(c),
    ).length;
  }

  getExtensionContactsCount(): number {
    return this.allContacts().filter((c: Contact) =>
      ContactUtils.isExtensionContact(c),
    ).length;
  }

  getSelectedManualContactsCount(): number {
    return this.selectedContactIds.filter(
      (ref: ContactReference) => ref.source === ContactSource.MANUAL,
    ).length;
  }

  getSelectedExtensionContactsCount(): number {
    return this.selectedContactIds.filter(
      (ref: ContactReference) => ref.source === ContactSource.EXTENSION,
    ).length;
  }

  selectAllDisplayedContacts(): void {
    const displayedContacts = this.displayedContacts();
    const currentSelectionIds = this.selectedContactIds.map(
      (ref: ContactReference) => `${ref.contactId}-${ref.source}`,
    );

    // Find new contacts to add
    const newContacts = displayedContacts.filter(
      (contact: Contact) =>
        !currentSelectionIds.includes(`${contact.contactId}-${contact.source}`),
    );

    // Add new contacts with proper order numbers
    let currentOrder = this.selectedContactIds.length;
    const newContactRefs: ContactReference[] = newContacts.map((contact) => ({
      contactId: contact.contactId,
      source: contact.source!,
      order: currentOrder++,
    }));

    this.selectedContactIds = [...this.selectedContactIds, ...newContactRefs];
  }

  clearAllSelections(): void {
    this.selectedContactIds = [];
  }

  getSelectedContactsCount(): number {
    return this.selectedContactIds.length;
  }

  validateGroupName(): void {
    this.groupNameError = "";

    if (!this.groupName.trim()) {
      this.groupNameError = this.translate.instant(
        "ASTIMAX.CONTACT.WIDGET.GROUP_NAME_REQUIRED",
      );
      return;
    }

    if (this.groupName.trim().length < 2) {
      this.groupNameError = this.translate.instant(
        "ASTIMAX.CONTACT.WIDGET.GROUP_NAME_TOO_SHORT",
      );
      return;
    }

    if (this.groupName.trim().length > 50) {
      this.groupNameError = this.translate.instant(
        "ASTIMAX.CONTACT.WIDGET.GROUP_NAME_TOO_LONG",
      );
      return;
    }

    // Check for duplicate group names
    const existingNames = this.data.existingGroups.map((g) =>
      g.name.toLowerCase(),
    );
    if (existingNames.includes(this.groupName.trim().toLowerCase())) {
      this.groupNameError = this.translate.instant(
        "ASTIMAX.CONTACT.WIDGET.GROUP_NAME_EXISTS",
      );
    }
  }

  // Form validation and actions (valide without selected contacts)
  isFormValid(): boolean {
    return this.groupName.trim().length >= 2 && !this.groupNameError;
  }

  onCancel(): void {
    // Simple confirmation for unsaved changes
    if (this.manualContactsChanged) {
      const changesCount =
        this.createdManualContacts.length +
        this.updatedManualContacts.length +
        this.deletedManualContactIds.length;
      const confirmed = confirm(
        `You have ${changesCount} unsaved manual contact change(s). Are you sure you want to cancel?`,
      );

      if (confirmed) {
        this.dialogRef.close();
      }
    } else {
      this.dialogRef.close();
    }
  }

  async onCreateGroup(): Promise<void> {
    if (!this.isFormValid()) {
      this.validateGroupName();
      this.groupNameInput.control.markAsTouched();
      return;
    }

    if (!this.contactWidget?._id) {
      console.error(
        "[ContactGroupCreate] Cannot create group - widget ID missing",
      );
      return;
    }

    try {
      // Handle manual contact changes first
      if (this.manualContactsChanged) {
        await this.saveManualContactChanges();
      }

      // Normalize order numbers before saving
      const normalizedContactRefs = ContactUtils.normalizeOrderNumbers(
        this.selectedContactIds,
      );

      // Create the new group
      await this.createNewGroup(this.groupName.trim(), normalizedContactRefs);

      // Save the complete widget settings to database
      await this.contactWidgetService.saveContactWidgetSettingsToDatabase(
        this.contactWidget._id,
        this.contactWidget,
      );

      this.snackBar.open(
        `${this.translate.instant("ASTIMAX.CONTACT.WIDGET.GROUP")} "${this.groupName.trim()}" ${this.translate.instant("ASTIMAX.CONTACT.WIDGET.CREATED_SUCCESSFULLY_WITH")} ${normalizedContactRefs.length} ${this.translate.instant("ASTIMAX.CONTACT.WIDGET.CONTACTS")}`,
        "Close",
        { duration: 3000 },
      );

      // Close dialog with success result
      this.dialogRef.close({
        widget: this.contactWidget,
        groupName: this.groupName.trim(),
        selectedContactIds: normalizedContactRefs,
        success: true,
      });
    } catch (error) {
      console.error("[ContactGroupCreate] Error creating group:", error);
      this.snackBar.open(
        this.translate.instant("ASTIMAX.CONTACT.WIDGET.FAILED_TO_CREATE_GROUP"),
        "Close",
        { duration: 3000 },
      );
    }
  }

  private async createNewGroup(
    groupName: string,
    contacts: ContactReference[],
  ): Promise<void> {
    if (!this.contactWidget?._id) {
      throw new Error("Widget ID missing");
    }

    // Generate new group ID
    const newGroupId = this.generateNewGroupId();

    const newGroup: ContactGroup = {
      groupId: newGroupId,
      name: groupName,
      contacts: contacts,
      created: new Date(),
    };

    // Get current groups from service
    const widgetData = this.contactWidgetService.getWidgetData(
      this.contactWidget._id,
    );
    if (!widgetData) {
      throw new Error("Widget data not found");
    }

    const currentGroups = widgetData.contactGroups;
    const updatedGroups = [...currentGroups, newGroup];

    // Update contact groups through service
    this.contactWidgetService.updateContactGroups(
      this.contactWidget._id,
      updatedGroups,
    );

    // Update widget settings
    if (this.contactWidget.settings) {
      this.contactWidget.settings.contactGroups = updatedGroups;
    }
  }

  private async saveManualContactChanges(): Promise<void> {
    if (!this.contactWidget?._id) {
      throw new Error("[ContactGroupCreate] Widget ID missing");
    }

    // Early exit if no changes to process
    if (!this.hasManualContactChanges()) {
      console.log("[ContactGroupCreate] No manual contact changes to save");
      return;
    }

    const widgetId = this.contactWidget._id;

    try {
      // Get current manual contacts from service (authoritative source)
      const widgetData = this.contactWidgetService.getWidgetData(widgetId);
      if (!widgetData) {
        throw new Error("[ContactGroupCreate] Widget data not found");
      }

      // Apply all changes
      const updatedManualContacts = this.applyManualContactChanges(
        widgetData.manualContacts,
      );

      const totalChanges =
        this.createdManualContacts.length +
        this.updatedManualContacts.length +
        this.deletedManualContactIds.length;

      console.log(
        `[ContactGroupCreate] Applying ${totalChanges} manual contact changes: ${widgetData.manualContacts.length} → ${updatedManualContacts.length} contacts`,
      );

      // Update service with processed contacts
      this.contactWidgetService.updateManualContacts(
        widgetId,
        updatedManualContacts,
        true,
      );

      // Sync local allContacts signal with updated service data
      await this.syncAllContacts(widgetId);

      console.log(
        `[ContactGroupCreate] Successfully processed manual contact changes`,
      );
    } catch (error) {
      console.error(
        "[ContactGroupCreate] Error saving manual contact changes:",
        error,
      );
      throw error;
    } finally {
      // Always clear tracking arrays, even if there was an error
      this.clearChangeTracking();
    }
  }

  /**
   * Check if there are any manual contact changes to process
   */
  private hasManualContactChanges(): boolean {
    return (
      this.createdManualContacts.length > 0 ||
      this.updatedManualContacts.length > 0 ||
      this.deletedManualContactIds.length > 0
    );
  }

  /**
   * Apply all manual contact changes
   */
  private applyManualContactChanges(
    currentContacts: ManualContact[],
  ): ManualContact[] {
    const contactsMap = new Map<string, ManualContact>();

    // Add existing contacts to map
    currentContacts.forEach((contact) => {
      contactsMap.set(contact.contactId, { ...contact });
    });

    // Apply created contacts (only if they don't exist)
    let createdCount = 0;
    this.createdManualContacts.forEach((createdContact) => {
      if (!contactsMap.has(createdContact.contactId)) {
        contactsMap.set(createdContact.contactId, { ...createdContact });
        createdCount++;
      }
    });

    // Apply updates (overwrite existing)
    let updatedCount = 0;
    this.updatedManualContacts.forEach((updatedContact) => {
      if (contactsMap.has(updatedContact.contactId)) {
        contactsMap.set(updatedContact.contactId, { ...updatedContact });
        updatedCount++;
      }
    });

    // Apply deletions
    let deletedCount = 0;
    this.deletedManualContactIds.forEach((contactId) => {
      if (contactsMap.delete(contactId)) {
        deletedCount++;
      }
    });

    // Log individual change counts
    if (createdCount > 0)
      console.log(
        `[ContactGroupCreate] Added ${createdCount} created contacts`,
      );
    if (updatedCount > 0)
      console.log(`[ContactGroupCreate] Updated ${updatedCount} contacts`);
    if (deletedCount > 0)
      console.log(`[ContactGroupCreate] Deleted ${deletedCount} contacts`);

    // Convert map back to array
    return Array.from(contactsMap.values());
  }

  /**
   * Sync allContacts signal with updated service data
   */
  private async syncAllContacts(widgetId: string): Promise<void> {
    const updatedWidgetData = this.contactWidgetService.getWidgetData(widgetId);
    if (!updatedWidgetData) {
      console.warn(
        "[ContactGroupCreate] Could not sync allContacts - widget data not found",
      );
      return;
    }

    // Preserve extension contacts from current allContacts signal
    const currentExtensionContacts = this.allContacts().filter(
      (c: Contact): c is ExtensionContact => ContactUtils.isExtensionContact(c),
    );

    // Create new synced contacts array
    const syncedContacts: Contact[] = [
      ...updatedWidgetData.manualContacts,
      ...currentExtensionContacts,
    ];

    // Update the signal
    this.allContacts.set(syncedContacts);

    console.log(
      `[ContactGroupCreate] Synced allContacts() - ${updatedWidgetData.manualContacts.length} manual + ${currentExtensionContacts.length} extension = ${syncedContacts.length} total contacts`,
    );
  }

  /**
   * Clear all change tracking arrays and flags
   */
  private clearChangeTracking(): void {
    this.createdManualContacts.length = 0;
    this.updatedManualContacts.length = 0;
    this.deletedManualContactIds.length = 0;
    this.manualContactsChanged = false;
  }

  private generateNewGroupId(): number {
    // Get current groups from service
    const widgetData = this.contactWidgetService.getWidgetData(
      this.contactWidget!._id,
    );
    const currentGroups = widgetData?.contactGroups || [];

    return currentGroups.length > 0
      ? Math.max(...currentGroups.map((g: ContactGroup) => Number(g.groupId))) +
          1
      : 1;
  }
}
